/**
 * Admin User Management Page
 * Manage all users (students, teachers, admins)
 */

import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout, ActionDropdown, ConfirmModal } from '../../components';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    role: 'student',
    status: 'active',
  });
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', variant: 'danger', icon: 'warning', confirmText: '', onConfirm: null });

  // Use ref to prevent duplicate calls on mount
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once on initial mount, then on filter/search changes
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
    }
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchQuery]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch all users for accurate counts, then filter client-side
      const params = {
        page: 1,
        limit: 1000, // Fetch a large number to get all users for counts
        ...(searchQuery && { search: searchQuery }),
      };
      const data = await api.getUsers(params);
      const allUsers = data.users || [];
      setUsers(allUsers);
      setPagination(data.pagination || { page: 1, limit: 1000, total: allUsers.length, pages: 1 });
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMessage = error.message || 'Failed to fetch users';
      setError(errorMessage);
      // If 404, it might be an authentication issue
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        setError('API endpoint not found. Please check if you are logged in as an admin.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      role: user.role,
      status: user.status,
    });
    setError('');
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Only send role and status for updates
      const updateData = {
        role: formData.role,
        status: formData.status,
      };
      await api.updateUser(selectedUser.id || selectedUser._id, updateData);
      setShowEditModal(false);
      fetchUsers();
      setFormData({
        role: 'student',
        status: 'active',
      });
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message || 'Failed to update user');
    }
  };

  const handleToggleStatus = (user) => {
    const userId = user.id || user._id;
    if (userId === currentUser?.id || userId === currentUser?._id) {
      alert('You cannot deactivate your own account');
      return;
    }
    const isActive = user.status === 'active';
    setConfirm({
      open: true,
      title: isActive ? 'Deactivate User?' : 'Activate User?',
      message: isActive
        ? `${user.name} will no longer be able to access the platform.`
        : `${user.name} will regain access to the platform.`,
      variant: isActive ? 'warning' : 'info',
      icon: 'warning',
      confirmText: isActive ? 'Deactivate' : 'Activate',
      onConfirm: async () => {
        try {
          const newStatus = isActive ? 'inactive' : 'active';
          await api.updateUserStatus(userId, newStatus);
          fetchUsers();
        } catch (err) {
          alert(err.message || 'Failed to update user status');
        }
        setConfirm(prev => ({ ...prev, open: false }));
      },
    });
  };

  // Calculate counts from all users
  const userCounts = {
    all: users.length,
    student: users.filter(u => u.role === 'student').length,
    teacher: users.filter(u => u.role === 'teacher').length,
    admin: users.filter(u => u.role === 'admin').length,
    inactive: users.filter(u => u.status === 'inactive').length,
  };

  const filteredUsers = users.filter((user) => {
    if (filter === 'all') return true;
    if (filter === 'inactive') return user.status === 'inactive';
    return user.role === filter;
  });

  return (
    <AdminLayout>
      <SEO title="User Management" noIndex />
          {/* Header */}
          <div className="lh-user-management__header flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="lh-user-management__header-info">
              <h1 className="lh-user-management__title text-2xl md:text-3xl font-bold text-dark-900 mb-1">
                User Management
              </h1>
              <p className="lh-user-management__subtitle text-dark-500">
                Manage students, teachers, and admins on the platform
              </p>
            </div>
            <div className="lh-user-management__header-actions flex items-center gap-3">
              <div className="lh-user-management__search relative">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="lh-user-management__search-input input-field pl-10 py-2 w-64"
                />
                <svg className="lh-user-management__search-icon w-5 h-5 text-dark-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {/* Filter Dropdown */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="lh-user-management__filter-select px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500 bg-white text-dark-700 font-medium text-sm appearance-none pr-8 cursor-pointer"
                >
                  <option value="all">All Users ({userCounts.all})</option>
                  <option value="student">Students ({userCounts.student})</option>
                  <option value="teacher">Teachers ({userCounts.teacher})</option>
                  <option value="admin">Admins ({userCounts.admin})</option>
                  <option value="inactive">Inactive ({userCounts.inactive})</option>
                </select>
                <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>


          {/* Users Table */}
          <div className="lh-user-management__table card overflow-hidden min-h-[320px]">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                <p className="mt-4 text-dark-500">Loading users...</p>
              </div>
            ) : (
              <>
            {/* Desktop Table */}
            <div className="lh-user-management__desktop hidden md:block overflow-x-auto">
              <table className="lh-user-management__table-element w-full">
                <thead className="lh-user-management__table-head bg-dark-50">
                  <tr className="lh-user-management__table-row">
                        <th className="lh-user-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Full Name</th>
                        <th className="lh-user-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Email</th>
                    <th className="lh-user-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Role</th>
                        <th className="lh-user-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Status</th>
                    <th className="lh-user-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Joined</th>
                    <th className="lh-user-management__table-th text-right p-4 text-sm font-semibold text-dark-600 w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="lh-user-management__table-body divide-y divide-dark-100">
                      {filteredUsers.map((user) => {
                        const isCurrentUser = (user.id || user._id) === (currentUser?.id || currentUser?._id);
                        return (
                          <tr key={user.id || user._id} className="lh-user-management__user-row hover:bg-dark-50">
                            <td className="lh-user-management__user-cell p-4">
                              <div className="lh-user-management__user-info flex items-center gap-3">
                                <div className="lh-user-management__user-avatar w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                                  {user.avatar ? (
                                    <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                                  ) : (
                                    <span>{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                                  )}
                                </div>
                                <p className="lh-user-management__user-name font-medium text-dark-900">{user.name}</p>
                              </div>
                            </td>
                            <td className="lh-user-management__user-cell p-4 text-sm text-dark-600">{user.email}</td>
                            <td className="lh-user-management__user-cell p-4">
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                                user.role === 'admin' ? 'bg-red-100 text-red-600' : user.role === 'teacher' ? 'bg-purple-100 text-purple-600' : 'bg-primary-100 text-primary-600'
                              }`}>{user.role}</span>
                            </td>
                            <td className="lh-user-management__user-cell p-4">
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>{user.status}</span>
                            </td>
                            <td className="lh-user-management__user-cell p-4 text-sm text-dark-500">{formatDate(user.createdAt)}</td>
                            <td className="lh-user-management__user-cell p-4 text-right">
                              <ActionDropdown actions={[
                                {
                                  label: 'Edit User',
                                  icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
                                  onClick: () => handleEditUser(user),
                                  hidden: isCurrentUser,
                                },
                                {
                                  label: user.status === 'active' ? 'Deactivate User' : 'Activate User',
                                  icon: user.status === 'active'
                                    ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                                  variant: user.status === 'active' ? 'danger' : 'success',
                                  onClick: () => handleToggleStatus(user),
                                  hidden: isCurrentUser,
                                  divider: true,
                                },
                              ]} />
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lh-user-management__mobile md:hidden divide-y divide-dark-100">
                  {filteredUsers.map((user) => {
                    const isCurrentUser = (user.id || user._id) === (currentUser?.id || currentUser?._id);
                    return (
                      <div key={user.id || user._id} className="lh-user-management__mobile-card p-4">
                  <div className="lh-user-management__mobile-header flex items-start justify-between mb-3">
                    <div className="lh-user-management__mobile-user flex items-center gap-3">
                            <div className="lh-user-management__mobile-avatar w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span>{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                              )}
                            </div>
                      <div className="lh-user-management__mobile-info">
                        <p className="lh-user-management__mobile-name font-medium text-dark-900">{user.name}</p>
                        <p className="lh-user-management__mobile-email text-sm text-dark-500">{user.email}</p>
                      </div>
                    </div>
                    <span className={`lh-user-management__mobile-status text-xs font-semibold px-2 py-0.5 rounded-full ${
                      user.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {user.status}
                    </span>
                  </div>
                  <div className="lh-user-management__mobile-footer flex items-center justify-between">
                    <div className="lh-user-management__mobile-meta flex items-center gap-4 text-sm text-dark-500">
                      <span className={`lh-user-management__mobile-role px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-red-100 text-red-600'
                                : user.role === 'teacher'
                                ? 'bg-purple-100 text-purple-600'
                                : 'bg-primary-100 text-primary-600'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                          {!isCurrentUser && (
                            <ActionDropdown actions={[
                              {
                                label: 'Edit User',
                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
                                onClick: () => handleEditUser(user),
                              },
                              {
                                label: user.status === 'active' ? 'Deactivate' : 'Activate',
                                icon: user.status === 'active'
                                  ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                  : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                                variant: user.status === 'active' ? 'danger' : 'success',
                                onClick: () => handleToggleStatus(user),
                                divider: true,
                              },
                            ]} />
                          )}
                </div>
                      </div>
                    );
                  })}
            </div>

            {filteredUsers.length === 0 && (
              <div className="lh-user-management__empty p-12 text-center">
                <svg className="lh-user-management__empty-icon w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="lh-user-management__empty-title text-lg font-semibold text-dark-900 mb-1">No users found</h3>
                <p className="lh-user-management__empty-text text-dark-500">Try adjusting your search or filters</p>
              </div>
            )}
              </>
            )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-dark-900 mb-4">Edit User</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={selectedUser.name || ''}
                    disabled
                    readOnly
                    className="w-full px-4 py-2 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-dark-400">Name cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={selectedUser.email || ''}
                    disabled
                    readOnly
                    className="w-full px-4 py-2 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-dark-400">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              {error && (
                <div className="mt-4 p-3 bg-red-100 text-red-600 rounded-lg text-sm">{error}</div>
              )}
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-dark-200 rounded-lg text-dark-700 hover:bg-dark-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirm.open}
        onClose={() => setConfirm(prev => ({ ...prev, open: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        confirmText={confirm.confirmText}
        variant={confirm.variant}
        icon={confirm.icon}
      />
    </AdminLayout>
  );
};

export default UserManagement;
